import { Observation, Organization, Practitioner } from "@medplum/fhirtypes";
import { cloneDeep } from "lodash";
import { MappedConsolidatedResources } from "../../../shared-logic/consolidated-context/reducer";
import { SidePanelContent } from "../../side-panel/side-panel-content";
import { formatDate, getReferenceResources, getResourcesFromBundle } from "../shared";
import { CodesDisplay } from "../shared/codes";
import { DetailsDisplay } from "../shared/details";
import { OccurenceDisplayText } from "../shared/occurrences";
import { showPeriodDate } from "../shared/period";
import { ReferencesDisplay } from "../shared/references";
import { SidePanelSection } from "../shared/sidepanel-section";
import { VitalsChart } from "../vitals/chart";
import { GroupedLabs, LabRowData, getLabs } from "./shared";
import { groupLabs } from "./table-data";

export function LabContent({
  mappedConsolidated,
  isImpersonating,
  ...rowContents
}: {
  isImpersonating?: boolean;
  mappedConsolidated: MappedConsolidatedResources | undefined;
} & LabRowData) {
  const observations = getResourcesFromBundle<Observation>(mappedConsolidated, "Observation");
  const labs = getLabs(observations);
  const groupedLabs = groupLabs(labs);
  const selectedLab =
    groupedLabs.find(lab => lab.mostRecentObservation.id === rowContents.id) ??
    groupedLabs.find(lab => lab.sortedPoints?.some(p => p.id === rowContents.id));

  if (!selectedLab) {
    return null;
  }

  const mostRecentLab = selectedLab.mostRecentObservation;
  const valueString = mostRecentLab?.extension?.find(ext => ext.valueString)?.valueString ?? "";

  let labObservations: number[] | undefined = undefined;
  let dates: string[] | undefined = undefined;
  if (selectedLab.sortedPoints) {
    const labPointsDescOrder = cloneDeep(selectedLab.sortedPoints).reverse();
    labObservations = labPointsDescOrder.flatMap(p => p.numericValue || []);
    dates = labPointsDescOrder.map(p => p.date);
  }

  return (
    <SidePanelContent
      title="Lab"
      sourceDocument={{
        id: selectedLab.mostRecentObservation.id ?? "",
        fileName: valueString,
      }}
      fhirJson={isImpersonating ? JSON.stringify(mostRecentLab, null, 2) : undefined}
    >
      {labObservations && labObservations.length > 1 && dates && (
        <VitalsChart
          vitalsId={mostRecentLab.id ?? ""}
          vitals={{
            title: selectedLab.title,
            values: labObservations,
          }}
          dates={dates.map(formatDate)}
        />
      )}
      <LabDisplay
        lab={selectedLab}
        tableRow={rowContents}
        mappedConsolidated={mappedConsolidated}
      />
    </SidePanelContent>
  );
}

function LabDisplay({
  lab,
  tableRow,
  mappedConsolidated,
}: {
  lab: GroupedLabs;
  tableRow: LabRowData;
  mappedConsolidated: MappedConsolidatedResources | undefined;
}) {
  const performerOrgReferences = getReferenceResources<Organization>(
    lab.mostRecentObservation.performer,
    "Organization",
    mappedConsolidated
  );

  const performerPractitionerReferences = getReferenceResources<Practitioner>(
    lab.mostRecentObservation.performer,
    "Practitioner",
    mappedConsolidated
  );
  const pointsDescOrder = [...(lab.sortedPoints || [])];

  return (
    <>
      <DetailsDisplay
        details={{
          observation: tableRow.observation,
          interpretation: tableRow.interpretation,
          "Reference Range": tableRow.referenceRange,
          period: showPeriodDate({
            period: lab.mostRecentObservation.effectivePeriod,
            fallbackDate: lab.mostRecentObservation.effectiveDateTime,
          }),
        }}
      />
      {pointsDescOrder.length > 0 && (
        <SidePanelSection title="Data Points" totalItems={pointsDescOrder.length}>
          {pointsDescOrder.map((p, index) =>
            OccurenceDisplayText(p.date, p.value.toString(), index)
          )}
        </SidePanelSection>
      )}
      <CodesDisplay code={lab.mostRecentObservation.code} />
      <ReferencesDisplay
        organizations={{
          refs: performerOrgReferences,
        }}
        practitioners={{
          refs: performerPractitionerReferences,
        }}
      />
    </>
  );
}
